import {
  ActionError,
  AttentionBlock,
  Button,
  FieldGrid,
  FormBlock,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  titleCase,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { Page } from '@/app/shell'
import { useClinicProfile } from '@/features/clinic-profile/api'
import { normalizeDocNumber } from '@/features/clinic-profile/doc-number'
import { placeFieldErrors } from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  useCreatePatient,
  usePatientAction,
  type Audience,
  type ExistingPatient,
  type PatientInput,
} from './api'
import {
  EMPTY_PATIENT_FORM,
  PATIENT_FORM_KEYS,
  QUICK_FORM_KEYS,
  changedBlocks,
  createPatientInput,
  isMinorOn,
  patientFieldKey,
  quickPatientInput,
  validatePatientForm,
  type PatientFormState,
} from './form'
import { AUDIENCES, personWords } from './labels'
import { MobileActionBar } from './mobile-action'
import {
  AddressBlock,
  CivilBlock,
  ConsentBlock,
  ContactBlock,
  GuardianBlock,
  IdentificationBlock,
  NameField,
  OriginBlock,
  WhatsappFields,
} from './patient-form-blocks'
import { GuardedLink, UnsavedDialog } from './unsaved-guard'
import { RECEPTION_IDENTITY_PATHS, useIdentityActions, type IdentityOptions } from '../identity/api'
import { RECEPTION_MESSAGES, prefillPatch, receptionDocument } from './identity'
import { ReceptionIdentityBlock } from './identity-block'
import { LeaveContext, useUnsavedGuard } from './use-unsaved-guard'

/**
 * Criar paciente (docs/areas/pacientes.md, fluxo "Criar paciente"; RN-PAC-01, 03 e 24).
 * Arquétipo: cadastro. O mínimo é nome e WhatsApp; o resto entra agora, depois pela ficha ou pela
 * anamnese. CPF que já existe leva à ficha existente em vez de criar outra. Abaixo de 900px a tela
 * pede só nome e WhatsApp: o cadastro completo continua no computador. A mesma tela cadastra aluno,
 * funcionário e terceiro (RN-PAC-36, RN-PAC-40): o público vem do segmento da lista
 * (`?audience=student`) e, na clínica com mais de um público, é o primeiro campo.
 */

/** O público que veio da lista, quando é um que existe; senão, paciente. */
function audienceFrom(value: string | null): Audience {
  return value && (AUDIENCES as string[]).includes(value) ? (value as Audience) : 'patient'
}

const FORM_ID = 'new-patient-form'

export function NewPatientPage() {
  const session = useSession()
  const navigate = useNavigate()
  const isDesk = useBreakpoint('desk')
  const [params] = useSearchParams()
  const create = useCreatePatient()
  const restore = usePatientAction()
  // Os públicos da clínica (RN-ADM-57): com mais de um, o cadastro pergunta o da pessoa.
  const clinic = useClinicProfile()
  const audiences = clinic.data?.audiences ?? []
  const [initial] = useState<PatientFormState>(() => ({
    ...EMPTY_PATIENT_FORM,
    audience: audienceFrom(params.get('audience')),
  }))
  const form = useFormState<PatientFormState>(
    () => initial,
    (values) => validatePatientForm(values),
  )
  const { values } = form
  const words = personWords(values.audience)
  // O documento repetido diz o tipo do número que foi digitado: CPF ou CNPJ (RN-PAC-38).
  const docKind = normalizeDocNumber(values.docNumber).length === 14 ? 'CNPJ' : 'CPF'
  const formRef = useRef<HTMLFormElement>(null)
  const guard = useUnsavedGuard(form.dirty && !create.isSuccess)

  // O preenchimento rápido (RN-PAC-58): o documento inteiro consulta a Westhetics uma vez; havendo
  // cadastro com e-mail em outra clínica, os dados (e o e-mail) só entram com o código.
  const identity = useIdentityActions(RECEPTION_IDENTITY_PATHS)
  const typedDocument = isDesk ? receptionDocument(values) : null
  const documentKey = typedDocument
    ? `${typedDocument.type}:${typedDocument.country ?? ''}:${typedDocument.number}`
    : ''
  const [found, setFound] = useState<{ key: string; options: IdentityOptions } | null>(null)
  const [prefillId, setPrefillId] = useState<string | null>(null)
  const lookedUp = useRef('')
  const { mutate: lookup } = identity.lookup
  useEffect(() => {
    if (!typedDocument || lookedUp.current === documentKey) return
    lookedUp.current = documentKey
    lookup(typedDocument, { onSuccess: (options) => setFound({ key: documentKey, options }) })
  }, [typedDocument, documentKey, lookup])
  const inWesthetics =
    found && found.key === documentKey && found.options.emails.length > 0 ? found : null
  const verified = inWesthetics && prefillId ? prefillId : null
  const emailLock = inWesthetics
    ? { hint: verified ? RECEPTION_MESSAGES.confirmed : RECEPTION_MESSAGES.locked }
    : undefined

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.check(isDesk ? PATIENT_FORM_KEYS : QUICK_FORM_KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    restore.reset()
    const base = isDesk ? createPatientInput(values) : quickPatientInput(values)
    // Sem o código, o e-mail não vai: a API o descartaria (RN-PAC-58).
    const input: PatientInput = inWesthetics
      ? {
          ...base,
          email: verified ? base.email : undefined,
          ...(verified ? { identity: { prefillId: verified } } : {}),
        }
      : base
    create.mutate(input, {
      onSuccess: (patient) => {
        toast(base.email && !patient.email ? RECEPTION_MESSAGES.omitted : 'Cadastro salvo.')
        navigate(routes.patient(patient.id))
      },
      onError: (error) => {
        form.refuse(placeFieldErrors<PatientFormState>(error, patientFieldKey).fields)
        focusFirstInvalid(formRef.current)
      },
    })
  }

  const failure = create.isError
    ? placeFieldErrors<PatientFormState>(create.error, patientFieldKey)
    : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)
  // CPF repetido: a API diz de quem é o cadastro, para abrir a ficha em vez de criar outra (RN-PAC-03).
  const existing =
    create.error && isApiError(create.error) && create.error.code === 'conflict'
      ? (create.error.details.existing as ExistingPatient | undefined)
      : undefined

  function restoreExisting(target: ExistingPatient) {
    restore.mutate(
      { id: target.id, action: 'restore' },
      {
        onSuccess: (patient) => {
          toast(`${titleCase(patient.name)} restaurada.`)
          navigate(routes.patient(patient.id))
        },
      },
    )
  }

  function focusDocNumber() {
    formRef.current?.querySelector<HTMLElement>('[data-field="docNumber"]')?.focus()
  }

  const submitButton = (
    <Button
      type="submit"
      form={FORM_ID}
      size={isDesk ? 'md' : 'lg'}
      loading={create.isPending}
      loadingLabel="Cadastrando…"
    >
      Cadastrar
    </Button>
  )
  const cancelButton = (
    <Button
      type="button"
      variant="outline"
      size={isDesk ? 'md' : 'lg'}
      onClick={() => guard.leave(routes.patients)}
    >
      Cancelar
    </Button>
  )

  return (
    <form id={FORM_ID} ref={formRef} onSubmit={submit} noValidate>
      <LeaveContext.Provider value={guard.leave}>
        <PageHeader
          LinkComponent={GuardedLink}
          crumbs={[{ label: words.titleMany, href: routes.patients }, { label: 'novo' }]}
        />
      </LeaveContext.Provider>
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title={`Novo ${words.one}`}
            subtitle={
              isDesk
                ? values.audience === 'patient'
                  ? 'Nome e WhatsApp bastam para cadastrar. O que faltar entra depois, pela ficha ou pela anamnese, e o medidor da ficha diz o que impede o contrato.'
                  : 'Nome e WhatsApp bastam para cadastrar. O que faltar entra depois, pela ficha, e o medidor da ficha diz o que impede o contrato.'
                : 'Nome e WhatsApp bastam para cadastrar agora.'
            }
          />

          <div className="mt-6 flex flex-col gap-4">
            {showFailure ? (
              <ActionError
                title={
                  existing
                    ? existing.archivedAt
                      ? `Este ${docKind} é de um cadastro arquivado`
                      : `Este ${docKind} já tem cadastro`
                    : 'Não foi possível cadastrar'
                }
                description={
                  failure.unplaced.length > 0
                    ? failure.unplaced.join(' ')
                    : existing
                      ? `${errorMessage(create.error)}${
                          existing.archivedAt && !session.canManageClinic
                            ? ' Peça a uma gestora para restaurar.'
                            : ''
                        }`
                      : `${errorMessage(create.error)} Nada foi criado; o que você digitou continua na tela.`
                }
                actions={
                  existing ? (
                    <>
                      {existing.archivedAt ? (
                        session.canManageClinic ? (
                          <Button
                            variant="outline"
                            size="sm"
                            loading={restore.isPending}
                            loadingLabel="Restaurando…"
                            onClick={() => restoreExisting(existing)}
                          >
                            Restaurar
                          </Button>
                        ) : null
                      ) : (
                        <Button variant="outline" size="sm" asChild>
                          <Link to={routes.patient(existing.id)}>Abrir ficha</Link>
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={focusDocNumber}>
                        Corrigir {docKind}
                      </Button>
                    </>
                  ) : undefined
                }
              />
            ) : null}

            {restore.isError ? (
              <ActionError
                title="Não foi possível restaurar o cadastro"
                description={`${errorMessage(restore.error)} Nada mudou.`}
                onDismiss={() => restore.reset()}
              />
            ) : null}

            {isDesk ? (
              <>
                <IdentificationBlock form={form} audiences={audiences} />
                {inWesthetics && typedDocument && !verified ? (
                  <ReceptionIdentityBlock
                    document={typedDocument}
                    options={inWesthetics.options.emails}
                    actions={identity}
                    onPrefilled={(id, prefill) => {
                      form.update(prefillPatch(prefill))
                      setPrefillId(id)
                      toast('Dados trazidos com o código. Confira antes de cadastrar.')
                    }}
                  />
                ) : null}
                <ContactBlock form={form} emailLock={emailLock} />
                <CivilBlock form={form} />
                <AddressBlock form={form} />
                <GuardianBlock form={form} isMinor={isMinorOn(values.birthDate)} />
                <ConsentBlock form={form} />
                <OriginBlock form={form} />
              </>
            ) : (
              <>
                <FormBlock
                  title="Cadastro rápido"
                  note="O nome vai para o contrato; o WhatsApp é por onde seguem a anamnese e os documentos."
                >
                  <FieldGrid>
                    <NameField
                      form={form}
                      name="name"
                      label="Nome completo"
                      className="col-span-full"
                    />
                    <WhatsappFields form={form} />
                  </FieldGrid>
                </FormBlock>
                <AttentionBlock variant="lilac" title="O cadastro completo continua no computador">
                  {values.audience === 'patient'
                    ? 'CPF, nascimento, endereço e estado civil entram pela ficha, no computador, ou pela anamnese que a paciente responde. A ficha mostra o que falta.'
                    : `${words.document}, endereço e o resto entram pela ficha, no computador. A ficha mostra o que falta.`}
                </AttentionBlock>
              </>
            )}
            {isDesk ? <FormFooter secondary={cancelButton} primary={submitButton} /> : null}
          </div>
        </ReadingColumn>
      </Page>

      {isDesk ? null : (
        <MobileActionBar>
          {cancelButton}
          {submitButton}
        </MobileActionBar>
      )}

      <UnsavedDialog
        open={guard.isAsking}
        blocks={isDesk ? changedBlocks(values, initial) : []}
        kept="O cadastro ainda não foi criado; nada foi enviado."
        onStay={guard.stay}
        onLeave={guard.confirm}
      />
    </form>
  )
}
